import React, { useState, useEffect } from 'react';
import { PolicyImpactResults } from '../../services/policyImpactService';
import { getBuildingsEnergyData } from '../../services/buildingsEnergyDataService';
import {
  RNG_SHARE_2045_BY_SCENARIO,
  getBuildingsBauPathway,
  getBuildingsBauDebugTable,
  INITIAL_STOCK_ELECTRIFIED_BASE_YEAR,
  type BuildingsBauScenario,
  type BuildingsBauDebugRow,
  type BuildingsBauPathway,
} from '../../services/buildingsBauPathways';
import {
  BuildingsStockChart,
  BuildingsEnergyChart,
  BuildingsEmissionsChart,
  BuildingsEmissionsStackChart,
  RngImpactChart,
  BuildingsBauComparisonChart,
} from './BuildingsElectrificationCharts';
import type { BuildingEmissionsTimeSeries } from './PolicyImpactTool';
import './BuildingElectrificationPolicy.css';

const EQUIPMENT_TURNOVER_RATE_PERCENT = 6;
const KWH_PER_THERM = 29.3;
const HEAT_PUMP_COP_DEFAULT = 3.5; // Efficient unit in mild climate
const HEAT_PUMP_COP_MIN = 2.5;
const HEAT_PUMP_COP_MAX = 4;

interface BuildingElectrificationPolicyProps {
  additionalElectrificationPercent: number;
  results: PolicyImpactResults | null;
  bauElectrifiedShare2045?: number | null;
  currentElectrifiedShare2023?: number | null;
  bauTherms2045?: number | null;
  bauKwh2045?: number | null;
  rngShare2045?: number | null;
  location?: string;
  buildingsBauScenario?: BuildingsBauScenario;
  /** RNG share 0–100 by 2045 (BAU assumption); controlled from this panel, drives model when set */
  buildingsRngPercent?: number;
  onBuildingsRngChange?: (value: number) => void;
  /** Heat pump COP (2.5–4); default 3.5. Controlled from this panel when onBuildingsCopChange is set. */
  buildingsCop?: number;
  onBuildingsCopChange?: (value: number) => void;
  /** Building-only emissions by year (from policy time series); for charts. */
  buildingEmissionsTimeSeries?: BuildingEmissionsTimeSeries | null;
  /** Grid decarbonization year (for BAU comparison chart g/kWh ramp). */
  cleanElectricityYear?: number;
  /** Explicit bill impact diagnostics for electrification interpretation. */
  billImpactDiagnostics?: {
    annualBillImpact: number;
    monthlyBillImpact: number;
    cumulativeBillImpact: number;
    annualBillImpactPerHome: number | null;
    monthlyBillImpactPerHome: number | null;
  } | null;
  /** Explicit price diagnostics for electrification interpretation. */
  priceDiagnostics?: {
    electricityPricePerKwh: number;
    gasPricePerTherm: number;
    gasEquivalentPricePerKwh: number;
    kwhPerThermEquivalent: number;
    priceSource: string;
  } | null;
  breakEvenElectricityPricePerKwh?: number | null;
}

const BuildingElectrificationPolicy: React.FC<BuildingElectrificationPolicyProps> = ({
  additionalElectrificationPercent,
  results,
  bauElectrifiedShare2045 = null,
  currentElectrifiedShare2023 = null,
  bauTherms2045 = null,
  bauKwh2045 = null,
  rngShare2045 = null,
  location,
  buildingsBauScenario = 'current_policy',
  buildingsRngPercent,
  onBuildingsRngChange,
  buildingsCop,
  onBuildingsCopChange,
  buildingEmissionsTimeSeries = null,
  cleanElectricityYear = 2045,
  billImpactDiagnostics = null,
  priceDiagnostics = null,
  breakEvenElectricityPricePerKwh = null,
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugTable, setDebugTable] = useState<BuildingsBauDebugRow[] | null>(null);
  const [pathway, setPathway] = useState<BuildingsBauPathway | null>(null);
  const [pathwaysByScenario, setPathwaysByScenario] = useState<Record<BuildingsBauScenario, BuildingsBauPathway> | null>(null);

  const effectiveCop = buildingsCop ?? HEAT_PUMP_COP_DEFAULT;
  const kwhPerThermDerived = KWH_PER_THERM / effectiveCop;
  const copControlled = onBuildingsCopChange != null && buildingsCop != null;
  const baseline = results?.baseline;
  const projected = results?.projected;
  const reductions = results?.reductions;
  const heatPumpContribution = results?.contributionsByPolicy?.heatPumpAdoption ?? 0;

  // BAU RNG: use panel control when provided (drives model); else show scenario default from baseline
  const bauRngPercent = buildingsRngPercent ?? (rngShare2045 != null ? rngShare2045 * 100 : RNG_SHARE_2045_BY_SCENARIO[buildingsBauScenario] * 100);
  const rngControlled = onBuildingsRngChange != null && buildingsRngPercent != null;
  const bauThermsDisplay = bauTherms2045 ?? baseline?.naturalGasTherms ?? null;
  const bauKwhDisplay = bauKwh2045 ?? baseline?.electricityKwh ?? null;
  const bauEmissionsMT = baseline != null ? baseline.naturalGasGHG + baseline.electricityGHG : null;

  const isBerkeley = location?.toLowerCase().includes('berkeley') ?? false;

  // Load pathway for charts (current scenario); and all three for BAU comparison chart
  useEffect(() => {
    if (!location || !buildingsBauScenario) {
      setPathway(null);
      setPathwaysByScenario(null);
      return;
    }
    let cancelled = false;
    getBuildingsEnergyData(location)
      .then((data) => {
        if (cancelled) return;
        setPathway(getBuildingsBauPathway(data, buildingsBauScenario));
        setPathwaysByScenario({
          conservative: getBuildingsBauPathway(data, 'conservative'),
          current_policy: getBuildingsBauPathway(data, 'current_policy'),
          accelerated: getBuildingsBauPathway(data, 'accelerated'),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPathway(null);
          setPathwaysByScenario(null);
        }
      });
    return () => { cancelled = true; };
  }, [location, buildingsBauScenario]);

  useEffect(() => {
    if (!isBerkeley || !location) {
      setDebugTable(null);
      return;
    }
    let cancelled = false;
    getBuildingsEnergyData(location)
      .then((data) => {
        if (cancelled) return;
        const gPerKwh2045 = 0;
        setDebugTable(getBuildingsBauDebugTable(data, { gPerKwh2045 }));
      })
      .catch(() => {
        if (!cancelled) setDebugTable(null);
      });
    return () => { cancelled = true; };
  }, [isBerkeley, location]);

  // With policy: from calculatePolicyImpact
  const remainingNgMT = projected?.naturalGasGHG ?? null;
  const electricityEmissionsMT = projected?.electricityGHG ?? null;
  const totalBuildingEmissionsWithPolicy =
    remainingNgMT != null && electricityEmissionsMT != null ? remainingNgMT + electricityEmissionsMT : null;

  const netGHGReduction = reductions?.totalReduction ?? null;
  const electricityEmissionsIncrease =
    baseline != null && projected != null
      ? Math.max(0, projected.electricityGHG - baseline.electricityGHG)
      : null;
  const billImpactDirectionLabel =
    billImpactDiagnostics == null
      ? null
      : billImpactDiagnostics.annualBillImpact > 0
        ? 'Electrification increases bills'
        : billImpactDiagnostics.annualBillImpact < 0
          ? 'Electrification lowers bills'
          : 'Electrification has neutral bill impact';

  return (
    <div className="building-electrification-policy">
      <div className="policy-details">
        {/* A. POLICY DESCRIPTION */}
        <div className="details-section">
          <h4>Policy Description</h4>
          <p>
            This policy accelerates the transition from fossil fuel-based building systems (e.g., natural gas
            furnaces and water heaters) to electric technologies (e.g., heat pumps).
          </p>
          <p>
            Under Business-As-Usual (BAU), building electrification already occurs over time due to equipment
            turnover, state policies, and market trends.
          </p>
          <p>
            This policy represents additional local action that accelerates electrification beyond BAU by
            increasing the share of fossil systems replaced with electric alternatives.
          </p>
          <p>
            As electrification increases:
          </p>
          <ul>
            <li>Natural gas consumption declines</li>
            <li>Electricity demand increases</li>
            <li>Emissions shift from direct combustion to the electricity grid</li>
          </ul>
        </div>

        {/* B. CRITICAL ASSUMPTIONS */}
        <div className="details-section">
          <h4>Critical Assumptions</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Initial electrified share (base year 2023)</span>
              <span className="result-value">{(INITIAL_STOCK_ELECTRIFIED_BASE_YEAR * 100).toFixed(0)}%</span>
              <div className="assumption-description">
                Defined in code as INITIAL_STOCK_ELECTRIFIED_BASE_YEAR. Starting point for stock evolution.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Electrified share in 2045 under BAU</span>
              <span className="result-value">
                {bauElectrifiedShare2045 != null
                  ? `${(bauElectrifiedShare2045 * 100).toFixed(1)}%`
                  : '—'}
              </span>
              {bauElectrifiedShare2045 == null && (
                <div className="assumption-description">From pathway when available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">Additional Building Electrification Beyond BAU</span>
              <span className="result-value">{additionalElectrificationPercent}%</span>
              <div className="assumption-description">
                Represents the additional share of remaining fossil-based systems converted to electric
                technologies by 2045.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Equipment Turnover Rate</span>
              <span className="result-value">{EQUIPMENT_TURNOVER_RATE_PERCENT}%</span>
            </div>
          </div>
        </div>

        {/* CHARTS: BAU evolution, energy transition, emissions, RNG */}
        <div className="details-section building-electrification-charts-section">
          <h4>Charts &amp; Figures</h4>
          <p className="building-electrification-charts-intro">
            These charts use the current BAU scenario, electrification slider, RNG setting, and clean electricity year. They update when you change any of those levers.
          </p>
          <div className="building-electrification-charts-grid">
            <BuildingsBauComparisonChart
              pathwaysByScenario={pathwaysByScenario}
              baseGPerKwh={baseline?.electricityCarbonIntensityGPerKwh ?? 300}
              cleanElectricityYear={cleanElectricityYear}
            />
            <BuildingsStockChart pathway={pathway} />
            <BuildingsEnergyChart pathway={pathway} />
            <BuildingsEmissionsChart buildingEmissionsTimeSeries={buildingEmissionsTimeSeries} />
            <BuildingsEmissionsStackChart buildingEmissionsTimeSeries={buildingEmissionsTimeSeries} />
            <RngImpactChart pathway={pathway} rngOverridePercent={buildingsRngPercent} />
          </div>
        </div>

        {/* C. ADVANCED ASSUMPTIONS: COP, kWh per therm, RNG */}
        <div className="details-section">
          <button
            type="button"
            className="advanced-toggle"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? '−' : '+'} Advanced Assumptions
          </button>
          {advancedOpen && (
            <div className="advanced-content">
              <div className="result-item">
                <span className="result-label">Heat Pump Efficiency (COP)</span>
                {copControlled ? (
                  <>
                    <span className="result-value">{effectiveCop.toFixed(1)}</span>
                    <input
                      type="range"
                      min={HEAT_PUMP_COP_MIN}
                      max={HEAT_PUMP_COP_MAX}
                      step={0.1}
                      value={effectiveCop}
                      onChange={(e) => onBuildingsCopChange(Number(e.target.value))}
                      className="advanced-slider"
                      style={{ marginTop: 4, width: '100%', maxWidth: 200 }}
                    />
                    <div className="assumption-description">
                      Range 2.5–4. Default 3.5 = efficient units operating in mild climate.
                    </div>
                  </>
                ) : (
                  <span className="result-value">{effectiveCop.toFixed(1)}</span>
                )}
              </div>
              <div className="result-item">
                <span className="result-label">kWh per therm (heat pump)</span>
                <span className="result-value">{kwhPerThermDerived.toFixed(2)} = 29.3 / COP</span>
              </div>
              <div className="result-item">
                <span className="result-label">Renewable Natural Gas Share (BAU, 2045)</span>
                {rngControlled ? (
                  <>
                    <span className="result-value">{bauRngPercent.toFixed(0)}%</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={bauRngPercent}
                      onChange={(e) => onBuildingsRngChange(Number(e.target.value))}
                      className="advanced-slider"
                      style={{ marginTop: 4, width: '100%', maxWidth: 200 }}
                    />
                    <div className="assumption-description">
                      BAU assumption: share of remaining natural gas that is renewable by 2045. The 2045 summary uses this value directly; the time series ramps linearly from the base-year RNG share to this 2045 value.
                    </div>
                  </>
                ) : (
                  <>
                    <span className="result-value">{bauRngPercent.toFixed(0)}%</span>
                    <div className="assumption-description">
                      {buildingsBauScenario === 'conservative' && 'Conservative: 10% RNG by 2045.'}
                      {buildingsBauScenario === 'current_policy' && 'Current policy: 30% RNG by 2045.'}
                      {buildingsBauScenario === 'accelerated' && 'Accelerated: 32% RNG by 2045.'}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* D. CALCULATION RESULTS (same logic as PolicyImpactService; use existing results only) */}
        <div className="details-section">
          <h4>Calculation Results</h4>
          <div className="calculation-results">
            <h5 className="results-subheading">— BAU —</h5>
            <div className="result-item">
              <span className="result-label">BAU Natural Gas (2045 therms)</span>
              <span className="result-value">
                {bauThermsDisplay != null ? bauThermsDisplay.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </span>
              {bauTherms2045 == null && bauThermsDisplay != null && (
                <div className="assumption-description">Baseline (2023) until BAU pathway available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">BAU Electricity (2045 kWh)</span>
              <span className="result-value">
                {bauKwhDisplay != null ? bauKwhDisplay.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
              </span>
              {bauKwh2045 == null && bauKwhDisplay != null && (
                <div className="assumption-description">Baseline (2023) until BAU pathway available.</div>
              )}
            </div>
            <div className="result-item">
              <span className="result-label">BAU Emissions (MT CO₂e)</span>
              <span className="result-value">
                {bauEmissionsMT != null ? bauEmissionsMT.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
              {bauTherms2045 == null && (
                <div className="assumption-description">Baseline building emissions (2023).</div>
              )}
            </div>

            <h5 className="results-subheading">— With Policy —</h5>
            <div className="result-item">
              <span className="result-label">Remaining Natural Gas (MT CO₂e)</span>
              <span className="result-value">
                {remainingNgMT != null ? remainingNgMT.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Electricity Emissions (MT CO₂e)</span>
              <span className="result-value">
                {electricityEmissionsMT != null
                  ? electricityEmissionsMT.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Total Building Emissions (MT CO₂e)</span>
              <span className="result-value">
                {totalBuildingEmissionsWithPolicy != null
                  ? totalBuildingEmissionsWithPolicy.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>

            <h5 className="results-subheading">— Net Impact —</h5>
            <div className="result-item">
              <span className="result-label">Net GHG Reduction (MT CO₂e)</span>
              <span className="result-value">
                {netGHGReduction != null ? netGHGReduction.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
              </span>
            </div>
            <div className="result-item highlight">
              <span className="result-label">Reduction from Electrification (MT CO₂e)</span>
              <span className="result-value">
                {heatPumpContribution.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Increase in Electricity Emissions (MT CO₂e)</span>
              <span className="result-value">
                {electricityEmissionsIncrease != null
                  ? electricityEmissionsIncrease.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : '—'}
              </span>
            </div>
            {billImpactDiagnostics && (
              <>
                <h5 className="results-subheading">— Bill Impact (Operating Cost) —</h5>
                <div className="result-item highlight">
                  <span className="result-label">{billImpactDirectionLabel}</span>
                  <span className="result-value">
                    Annual: ${billImpactDiagnostics.annualBillImpact.toLocaleString(undefined, { maximumFractionDigits: 0 })} / year
                  </span>
                  <div className="assumption-description">
                    Positive = bills increase; negative = bills decrease.
                  </div>
                </div>
                <div className="result-item">
                  <span className="result-label">Monthly bill change</span>
                  <span className="result-value">
                    ${billImpactDiagnostics.monthlyBillImpact.toLocaleString(undefined, { maximumFractionDigits: 0 })} / month
                  </span>
                </div>
                <div className="result-item">
                  <span className="result-label">Cumulative bill change (to 2045)</span>
                  <span className="result-value">
                    ${billImpactDiagnostics.cumulativeBillImpact.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                {billImpactDiagnostics.annualBillImpactPerHome != null && (
                  <div className="result-item">
                    <span className="result-label">Annual bill change per home</span>
                    <span className="result-value">
                      ${billImpactDiagnostics.annualBillImpactPerHome.toLocaleString(undefined, { maximumFractionDigits: 0 })} / home-year
                    </span>
                  </div>
                )}
                {billImpactDiagnostics.monthlyBillImpactPerHome != null && (
                  <div className="result-item">
                    <span className="result-label">Monthly bill change per home</span>
                    <span className="result-value">
                      ${billImpactDiagnostics.monthlyBillImpactPerHome.toLocaleString(undefined, { maximumFractionDigits: 0 })} / home-month
                    </span>
                  </div>
                )}
              </>
            )}
            {priceDiagnostics && (
              <>
                <h5 className="results-subheading">— Comparable Energy Prices —</h5>
                <div className="result-item">
                  <span className="result-label">Electricity price</span>
                  <span className="result-value">${priceDiagnostics.electricityPricePerKwh.toFixed(3)} / kWh</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Natural gas price</span>
                  <span className="result-value">${priceDiagnostics.gasPricePerTherm.toFixed(3)} / therm</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Natural gas electric-equivalent price</span>
                  <span className="result-value">${priceDiagnostics.gasEquivalentPricePerKwh.toFixed(3)} / kWh-e</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Break-even electricity price</span>
                  <span className="result-value">
                    ${((breakEvenElectricityPricePerKwh ?? priceDiagnostics.gasEquivalentPricePerKwh)).toFixed(3)} / kWh
                  </span>
                  <div className="assumption-description">
                    Source: {priceDiagnostics.priceSource}. kWh per therm equivalent = {priceDiagnostics.kwhPerThermEquivalent.toFixed(2)}.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Berkeley debug table */}
        {isBerkeley && (
          <div className="details-section">
            <button
              type="button"
              className="advanced-toggle"
              onClick={() => setDebugOpen(!debugOpen)}
              aria-expanded={debugOpen}
            >
              {debugOpen ? '−' : '+'} Debug: Berkeley BAU comparison
            </button>
            {debugOpen && debugTable && (
              <div className="advanced-content" style={{ overflowX: 'auto' }}>
                <p className="assumption-description" style={{ marginBottom: 8 }}>
                  Stock electrified share and 2045 outcomes by BAU scenario. Current policy slider 0 = BAU; slider 100 = full local electrification of remaining fossil load (grid clean 2045).
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Scenario</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2023</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2030</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Stock 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Therms 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>kWh 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>RNG 2045</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>Bldg MT 2045</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugTable.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '6px 8px' }}>
                          {row.scenario === 'current_policy_slider_0' ? 'Current policy (slider 0)' : row.scenario === 'current_policy_slider_100' ? 'Current policy (slider 100)' : row.scenario}
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2023 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2030 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.stockElectrified2045 * 100).toFixed(1)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.therms2045.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.kwh2045.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(row.rngShare2045 * 100).toFixed(0)}%</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>{row.buildingEmissions2045MT.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {debugOpen && !debugTable && (
              <div className="advanced-content"><p className="assumption-description">Loading…</p></div>
            )}
          </div>
        )}

        {/* E. METHODOLOGY */}
        <div className="details-section">
          <h4>Methodology</h4>
          <p>
            Building emissions are calculated using a stock-based model. Sales electrification
            curves use <strong>linear ramps</strong> to scenario target years (e.g. 100% electric sales by 2035/2045);
            a logistic/S-curve could be added later for smoother adoption.
          </p>
          <p>
            Under BAU, electrification occurs gradually as equipment is replaced over time based on turnover
            rates and appliance sales assumptions. This defines fossil fuel consumption and electricity demand
            over time.
          </p>
          <p>
            The policy increases electrification beyond BAU by converting a fraction of remaining fossil
            systems.
          </p>
          <p>Emissions are calculated as:</p>
          <ul>
            <li>Natural gas emissions (adjusted for renewable natural gas share)</li>
            <li>Electricity emissions (based on grid carbon intensity)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BuildingElectrificationPolicy;
